import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from 'AuthContext';
import ChangePasswordDialog from './ChangePasswordDialog';
import PDFViewerDialog from './PDFViewerDialog'
import { fetchWithAuth } from "utils/fetchWithAuth";
import { formatDate, formatMonth, capitalizeFirstLetter } from "utils/dateFormat";
import { labelForType } from "utils/stringFormat";
import {
  Alert, Button, Box, Card, CardContent, CircularProgress, Chip,
  Divider, IconButton, LinearProgress, Link, Pagination, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, 
  Tooltip, Typography
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset"

const getBailDocument = (docs) => {
  if (!docs) return null;

  const bailLocataire = docs.find(d => d.type === "bail_locataire");
  if (bailLocataire) return bailLocataire;

  return docs.find(d => d.type === "bail_bailleur") || null;
};

export default function FicheLocataire({ locataireId, mode = "readonly" }) {
  const [loc, setLoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [historiques, setHistoriques] = useState([]);
  const [loadingH, setLoadingH] = useState(false);
  const [paiements, setPaiements] = useState([]);
  const [page, setPage] = useState(1);
  const [documents, setDocuments] = useState(null);
  const [pdfDialog, setPdfDialog] = useState({ open: false, url: null });
  const [openMdpDialog, setOpenMdpDialog] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [mdpError, setMdpError] = useState("");
  const [mdpSuccess, setMdpSuccess] = useState("");
  const [mdpLoading, setMdpLoading] = useState(false);
  
  const { locataire_id, isLocataire, isAdmin } = useContext(AuthContext);
  const isUserAccount = isLocataire && String(locataireId) === String(locataire_id);
  const bail = getBailDocument(documents);
  const rowsPerPage = 10;
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!locataireId) return;
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/locataires/${locataireId}/`)
      .then((res) => res.json())
      .then((data) => {
        setLoc(data);
        setPaiements(data.derniere_location?.historique_paiements || []);
        setLoading(false);
      })
      .catch(() => {
        setError("Erreur réseau ou locataire introuvable.");
        setLoading(false);
      });
  }, [locataireId, API_URL]);

  const location = loc?.derniere_location || null;

  useEffect(() => {
    if (location?.id) {
      setHistoriques([]);
      setLoadingH(true);
      fetchWithAuth(`${API_URL}/api/loyers/historique/${location.id}/`)
        .then((res) => res.json())
        .then((data) => {
          setHistoriques(data);
          setLoadingH(false);
          setPage(1);
        })
        .catch(() => setLoadingH(false));
    }
  }, [location, API_URL]);

  useEffect(() => {
    if (openMdpDialog) {
      setMdpSuccess("");
      setMdpError("");
      setOldPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    }
  }, [openMdpDialog]);

  useEffect(() => {
    if (!location) return;
    
    fetchWithAuth(`${API_URL}/api/locations/${location.id}/documents/`)
      .then(res => res.json())
      .then(data => setDocuments(data));
  }, [location, API_URL]);


  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMdpError("");
    setMdpSuccess("");
    if (newPassword !== confirmNewPassword) {
      setMdpError("Les mots de passe ne correspondent pas");
      return;
    }
    setMdpLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/change-password/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setMdpSuccess("Mot de passe changé !");
        setOldPassword(""); setNewPassword(""); setConfirmNewPassword("");
      } else {
        setMdpError(data.detail || "Erreur lors du changement de mot de passe");
      }
    } catch (err) {
      setMdpError("Erreur lors de la connexion au serveur");
    }
    setMdpLoading(false);
  };

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
      <CircularProgress />
    </Box>
  );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!loc) return <Alert severity="error">Locataire non trouvé</Alert>;

  const garant = loc.garant;
  const bien = location?.lot?.bien;

  const historiqueFusionnee = historiques.map(loyer => {
    const paiementsMois = paiements.filter(p =>
        p.mois_concerne && p.mois_concerne.startsWith(loyer.mois)
    );
    return { ...loyer, paiements: paiementsMois };
  });

  const count = historiqueFusionnee.length;
  const paginatedHist = historiqueFusionnee.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  function openPdf(doc) {
    setPdfDialog({ open: true, url: new URL(doc.file, API_URL).href });
  }

  function closePdf() {
    setPdfDialog({ open: false, url: null });
  }

  return (
    <Card sx={{ maxWidth: 800, mx: "auto", mt: 4, mb: 4 }}>
      <CardContent>
        {isAdmin && loc.procedure_en_cours && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              maxWidth: 430,
              mx: "auto",
            }}
          >
            <b>Attention :</b> Ce locataire fait l'objet d'une procédure en cours
          </Alert>
        )}
        <Typography variant="h5" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {loc.prenom} {loc.nom}
          {isUserAccount && (
            <Tooltip title="Modifier mon mot de passe">
              <IconButton
                color="primary"
                size="small"
                sx={{ ml: 1 }}
                onClick={() => setOpenMdpDialog(true)}
              >
                <LockResetIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
        <Typography color="text.secondary">{loc.email}</Typography>
        <Typography color="text.secondary">{loc.telephone}</Typography>
        <Divider sx={{ my: 2 }} />

        {/* Bloc Location */}
        {location ? (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                {location.date_sortie ? "Dernière location" : "Location en cours"}
            </Typography>
            <Box sx={{ py: 1, px: 2, border: "1px solid #eee", borderRadius: 2, mb: 2 }}>
              <Typography>
                <b>Lot : </b> 
                {bien.est_residence && bien.nom_residence
                  ? `${bien.nom_residence} - ${location.lot?.nom || ""}`
                  : location.lot?.nom || ""
                }
              </Typography>
              {bien && (
                <Typography>
                  <b>Adresse du bien :</b> {bien.adresse}, {bien.code_postal} {bien.ville}
                </Typography>
              )}
              {(typeof location.loyer_nu !== "undefined" || typeof location.charges !== "undefined") && (
                <Typography>
                  <b>Loyer :</b> {(Number(location.loyer_nu || 0) + Number(location.charges || 0))} € 
                  {Number(location.charges) > 0 && (
                    <span> dont {location.charges} € charges</span>
                  )}
                </Typography>
              )}
              <Typography>
                <b>Caution :</b> {location.caution || 0} €
              </Typography>
              <Typography>
                <b>Entrée :</b> {formatDate(location.date_entree)}
              </Typography>
              {location.date_sortie && (
                <Typography>
                  <b>Sortie :</b> {formatDate(location.date_sortie)}
                </Typography>
              )}
            </Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
              Documents
            </Typography>
            <Box sx={{ border: "1px solid #eee", borderRadius: 2, p: 2 }}>
              {!documents ? (
                <Typography color="text.secondary">Chargement…</Typography>
              ) : (
                <>
                  <Typography fontWeight="bold">Bail</Typography>

                  {bail ? (
                    <Link
                      sx={{ cursor: "pointer", fontWeight: 500 }}
                      onClick={() => openPdf(bail)}
                    >
                      {labelForType(bail.type)}
                    </Link>
                  ) : (
                    <Typography color="text.secondary">
                      Aucun bail disponible
                    </Typography>
                  )}

                  <Typography fontWeight="bold" sx={{ mt: 2 }}>Révisions de loyer</Typography>

                  {documents.filter(d => d.type === "revision").length === 0 ? (
                    <Typography color="text.secondary">
                      Aucune révision de loyer disponible
                    </Typography>
                  ) : (
                    documents
                      .filter(d => d.type === "revision")
                      .map(q => (
                        <Link
                          key={q.id}
                          sx={{ cursor: "pointer", display: "block" }}
                          onClick={() => openPdf(q)}
                        >
                          {q.comment}
                        </Link>
                      ))
                  )}

                  <Typography fontWeight="bold" sx={{ mt: 2 }}>Quittances</Typography>

                  {documents.filter(d => d.type === "quittance").length === 0 ? (
                    <Typography color="text.secondary">
                      Aucune quittance disponible
                    </Typography>
                  ) : (
                    documents
                      .filter(d => d.type === "quittance")
                      .map(q => (
                        <Link
                          key={q.id}
                          sx={{ cursor: "pointer", display: "block" }}
                          onClick={() => openPdf(q)}
                        >
                          {formatMonth(q.comment.replace("Quittance ", ""))}
                        </Link>
                      ))
                  )}
                </>
              )}
            </Box>
          </>
        ) : (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>Aucune location enregistrée</Alert>
        )}

        {/* Bloc Garant */}
        {garant && (garant?.nom || garant?.prenom) && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Garant</Typography>
            <Paper elevation={2} sx={{ p: 1, mb: 2, background: "#f7fafb" }}>
              <Typography fontWeight="bold" fontSize={15}>
                {garant.prenom} {garant.nom}
              </Typography>
              <Typography fontSize={14}>
                {garant.adresse}, {garant.code_postal} {garant.ville}
              </Typography>
              <Typography fontSize={14}>
                {garant.email}
              </Typography>
              <Typography fontSize={14}>
                {garant.telephone}
              </Typography>
            </Paper>
          </>
        )}

        {/* Historique paiements */}
        {location && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
              Historique des paiements
            </Typography>
            {loadingH ? (
              <LinearProgress sx={{ mt: 1, mb: 2 }} />
            ) : historiques && historiques.length > 0 ? (
              <Box sx={{ py: 1, px: 2, border: "1px solid #eee", borderRadius: 2, mb: 2, overflowX: 'auto'}}>
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#f7fafb" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Mois</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Montant dû</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Paiements</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedHist.map((ligne, i) => (
                        <TableRow key={ligne.mois}>
                            <TableCell>
                                {capitalizeFirstLetter(
                                    (new Date(`${ligne.mois}-01`)).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
                                )}
                            </TableCell>
                            <TableCell>{ligne.montant} €</TableCell>
                            <TableCell>
                            {ligne.paiements.length === 0 ? (
                                <span>–</span>
                            ) : (
                                ligne.paiements.map(p => (
                                <div key={p.id}>
                                    {formatDate(p.date_paiement)} — {p.moyen_display || ""} ({p.montant} €)
                                </div>
                                ))
                            )}
                            </TableCell>
                            <TableCell>
                            <Chip
                                label={ligne.etat}
                                color={
                                  ligne.etat === "acquitté"
                                    ? "success"
                                  : ligne.etat === "en retard"
                                    ? "error"
                                  : ligne.etat === "dû|partiel"
                                    ? "warning"
                                  : "default"
                                }
                                size="small"
                            />
                            </TableCell>
                        </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {count > rowsPerPage && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={Math.ceil(count / rowsPerPage)}
                      page={page}
                      onChange={(_, newPage) => setPage(newPage)}
                      size="small"
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                Aucun paiement pour cette location
              </Typography>
            )}
          </>
        )}

        {loc.apl_beneficiaire && (
            <Paper sx={{ p: 2, mt: 2, background: '#EEF8FF', borderRadius: 2 }}>
                <Typography fontWeight="bold" sx={{ color: "#2196F3" }}>
                Montant actuel de l'APL
                </Typography>
                <Typography variant="h6">
                {loc.apl_montant ?? "Non renseigné"} €
                </Typography>
            </Paper>
        )}

        {/* Edition */}
        {mode !== "readonly" && (
          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button variant="contained" color="primary">Éditer</Button>
            <Button variant="outlined" color="error">Supprimer</Button>
          </Box>
        )}

        <ChangePasswordDialog
          open={openMdpDialog}
          onClose={(event, reason) => {
            if (reason && reason === "backdropClick") return;
            setOpenMdpDialog(false);
            setTimeout(() => {
              document.activeElement.blur();
            }, 0);
          }}
          oldPassword={oldPassword}
          setOldPassword={setOldPassword}
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          confirmNewPassword={confirmNewPassword}
          setConfirmNewPassword={setConfirmNewPassword}
          mdpError={mdpError}
          mdpSuccess={mdpSuccess}
          mdpLoading={mdpLoading}
          handleChangePassword={handleChangePassword}
        />

        {pdfDialog.open && (
          <PDFViewerDialog
            open={true}
            url={pdfDialog.url}
            onClose={closePdf}
          />
        )}
      </CardContent>
    </Card>
  );
}
